import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";
import { surfaceRoleClassName, type SurfaceRole } from "./surface-contract";

/**
 * Card variants synced from designs/design-tokens.pen
 *
 * .pen primitives mapped:
 *   ui/card-frame → variant="frame": glass-fill (#1C1C1E99),
 *                    backdrop-blur-glass, shadow-card-frame, border #FFFFFF1A
 *   ui/card-panel → variant="panel": panel fill (#0A0A0F99),
 *                    backdrop-blur-glass, shadow-card-panel, border #FFFFFF0D
 *   ui/card-modal → variant="modal": glass-fill-heavy,
 *                    backdrop-blur-[28px], shadow-card-modal, border #FFFFFF20
 *
 * Default keeps the existing surface style for backward compatibility.
 */
const cardVariants = cva("flex flex-col", {
  variants: {
    variant: {
      default: `${surfaceRoleClassName("frame")}`,
      /* ui/card-frame: canvas frame card */
      frame: `${surfaceRoleClassName("frame")}`,
      /* ui/card-panel: side panel card */
      panel: `${surfaceRoleClassName("panel")} border-glass-stroke-faint`,
      /* ui/card-modal: modal/dialog card */
      modal: `${surfaceRoleClassName("menu")}`,
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type CardProps = React.ComponentProps<"div"> &
  VariantProps<typeof cardVariants>;

function getCardSurfaceRole(variant: CardProps["variant"]): SurfaceRole {
  if (variant === "panel") return "panel";
  if (variant === "modal") return "menu";
  return "frame";
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      {...props}
      className={cn(cardVariants({ variant, className }))}
      data-denker-surface-role={getCardSurfaceRole(variant)}
    />
  ),
);
Card.displayName = "Card";

/**
 * CardHeader synced from .pen:
 *   Headers and body use AppKit spacing and typography tokens.
 */
const CardHeader = forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center border-b border-glass-stroke-subtle px-[20px] py-appkit-sm",
        className,
      )}
      {...props}
    />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = forwardRef<HTMLHeadingElement, React.ComponentProps<"h3">>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-appkit-body font-medium text-primary", className)}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = forwardRef<
  HTMLParagraphElement,
  React.ComponentProps<"p">
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-appkit-caption text-secondary", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

const CardContent = forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("px-[20px] py-appkit-md", className)}
      {...props}
    />
  ),
);
CardContent.displayName = "CardContent";

const CardFooter = forwardRef<HTMLDivElement, React.ComponentProps<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center px-[20px] pb-appkit-md pt-0",
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  cardVariants,
};
export type { CardProps };
